import { useEffect } from 'react';
import type { Source, StatusCounts, Summary } from '../types';
import { initBugTracker, TENANT_BUGTRACKER_KEY } from '../bugtracker';
import { useTheme } from '../theme';
import { AiButton } from './AiButton';
import { SOURCE_LABELS } from '../types';
import { SOURCE_ICON } from '../assets';
import { useLogo } from '../branding';

export type View =
  | 'overview' | Source | 'history' | 'hidden' | 'anomalies'
  | 'knowledge' | 'team' | 'roles' | 'clientusers' | 'policies' | 'alerts' | 'integrations' | 'reports'
  | 'changes' | 'mentions'
  | 'settings' | 'help' | 'wallboard';

interface Props {
  view: View;
  onNavigate: (v: View) => void;
  counts: { overall: StatusCounts; bySource: Record<Source, StatusCounts> };
  summary: Summary | null;
  email: string | null;
  /** Owners and admins get Integrations and the hide controls. */
  isManager: boolean;
  /**
   * Whether these two pages appear at all.
   *
   * Passed in rather than derived here: the sidebar should not be a second
   * place that decides permissions, because two places will eventually
   * disagree and the one nobody remembers is the one that leaks.
   */
  canViewHidden: boolean;
  canViewHistory: boolean;
  canViewChanges: boolean;
  /**
   * The wallboard is now switchable per plan, so it can be absent for reasons
   * that have nothing to do with the person looking at it.
   */
  canViewWallboard: boolean;
  onSignOut: () => void;
  onSyncNow: () => void;
  /** Opens the feature request board. */
  onFeatureRequest: () => void;
  /** Opens the AI assistant. */
  onAi: () => void;
  syncing: boolean;
}

const Ico = ({ d, size = 16 }: { d: string; size?: number }) => (
  <svg
    className="nav-svg" viewBox="0 0 24 24" width={size} height={size}
    fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    {d.split('|').map((p, i) => <path key={i} d={p} />)}
  </svg>
);

const PATH = {
  cog: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z|M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z',
  plug: 'M9 2v6|M15 2v6|M6 8h12v3a6 6 0 0 1-12 0V8z|M12 17v5',
  // A trace that steps up and down - status CHANGES, not status.
  pulse: 'M3 12h4l3 7 4-14 3 7h4',
  // An eye with a stroke through it: deliberately not being looked at.
  // Three heads: one in front, two behind. A group, not a single person.
  // An open book.
  book: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20|M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z|M12 7h5|M12 11h5',
  people: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2|M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8|M22 21v-2a4 4 0 0 0-3-3.87|M16 3.13a4 4 0 0 1 0 7.75',
  eyeOff: 'M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94|M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19|M14.12 14.12a3 3 0 1 1-4.24-4.24|M1 1l22 22',
  // Calendar with a tick: a schedule that has been reviewed.
  calendarCheck: 'M8 2v4|M16 2v4|M3 10h18|M5 4h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z|M9 15l2 2 4-4',
  bulb: 'M9 18h6|M10 22h4|M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z',
  // A bell. Alerts are the thing that reaches you when nobody is looking.
  bell: 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9|M13.73 21a2 2 0 0 1-3.46 0',
  // A screen on a stand. The wallboard is a thing you point at, not a page.
  monitor: 'M3 4h18v12H3z|M8 20h8|M12 16v4',
  // A key. Roles are what a door opens, not who is standing at it.
  key: 'M21 2l-2 2|M15.5 8.5a5 5 0 1 0-7 7 5 5 0 0 0 7-7z|M19 4l-6.5 6.5|M17 6l2 2',
  // A question mark in a circle. The one icon nobody has to be taught.
  help: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20|M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3|M12 17h.01',
  sun: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10z|M12 1v2|M12 21v2|M4.2 4.2l1.4 1.4|M18.4 18.4l1.4 1.4|M1 12h2|M21 12h2|M4.2 19.8l1.4-1.4|M18.4 5.6l1.4-1.4',
  moon: 'M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z',
};

/**
 * Left navigation.
 *
 * The footer holds a row of icon-only utilities — Settings and the bug
 * reporter, side by side, with room for Help and AI later. They are tools you
 * reach for occasionally, so they get an icon each rather than a labelled row
 * competing with the sources you are actually monitoring.
 */
export function Sidebar({
  view, onNavigate, counts, summary, email, isManager, canViewHidden, canViewHistory, canViewChanges,
  canViewWallboard,
  onSignOut, onSyncNow,
  onFeatureRequest, onAi, syncing,
}: Props) {
  const sources: Source[] = ['datto', 'sp', 'sc', 'msp', 'unitrends', 'email'];
  const [theme, toggleTheme] = useTheme();
  const logo = useLogo();

  // Initialised HERE, not in the app root: the widget needs #bugtracker-mount to
  // already be in the DOM or it falls back to a floating button in the page
  // corner. This component owns that node, so by the time this effect runs it
  // exists.
  useEffect(() => {
    if (!email) return;
    initBugTracker(TENANT_BUGTRACKER_KEY, { uid: email, email }, __APP_VERSION__);
  }, [email]);

  return (
    <nav className="sidebar">
      {/* The full wordmark gets the whole strip. A logo that already says the
          company name does not need the company name printed beside it, and the
          horizontal mark needs the width. Falls back to the bundled logo until
          an org uploads its own in Settings > Branding. */}
      <div className="sidebar-brand">
        {/* alt is empty ON PURPOSE. It used to read "Vital Technology Group" - one
            customer's name, hardcoded, shown to every other tenant. The logo is
            decorative here: the org is already named in the header and the
            switcher, so announcing it again is noise to a screen reader. */}
        <img className="brand-logo" src={logo} alt="" />
      </div>

      <div className="sidebar-scroll">
        <div className="nav-group">
          <div className="nav-group-label">Dashboard</div>
          {/* Beside Overview because it answers the same question at a
              different distance: Overview is the list you work, the wallboard
              is what the room sees. */}
          <NavItem label="Overview" active={view === 'overview'}
                   onClick={() => onNavigate('overview')} counts={counts.overall} />
          {canViewWallboard && (
            <NavItem label="Wallboard" active={view === 'wallboard'}
                     onClick={() => onNavigate('wallboard')} svg={<Ico d={PATH.monitor} />} />
          )}
        </div>

        <div className="nav-group">
          <div className="nav-group-label">Sources</div>
          {sources.map((s) => (
            <NavItem
              key={s}
              label={SOURCE_LABELS[s]}
              icon={SOURCE_ICON[s] ?? undefined}
              wide={s === 'sp' || s === 'sc'}
              active={view === s}
              onClick={() => onNavigate(s)}
              counts={counts.bySource[s]}
              stale={summary ? summary.lastSyncOk?.[s] === false : false}
            />
          ))}
        </div>

        <div className="nav-group">
          <div className="nav-group-label">Activity</div>
          {canViewHistory && <NavItem label="Status changes" active={view === 'history'}
                   onClick={() => onNavigate('history')} svg={<Ico d={PATH.pulse} />} />}
          {/* Beside Status changes deliberately: that page answers what the
              backup DID, this one answers whether it is still backing up what
              it used to. The two come apart silently, which is the whole
              reason this exists. Ungated like Reports - a technician who
              cannot see that a volume was dropped will spend an afternoon on
              the wrong question. */}
          {canViewChanges && (
            <NavItem label="Configuration changes" active={view === 'changes'}
                     onClick={() => onNavigate('changes')} svg={<Ico d={PATH.pulse} />} />
          )}
          {/* Promoted out of Settings. It is a queue of things awaiting a
              human decision, which is activity - not configuration you set
              once and forget. */}
          {/* Hidden machines are excluded from every count, so without a way to
              see them a suppression is invisible until someone remembers it. */}
          {canViewHidden && <NavItem label="Hidden Endpoints" active={view === 'hidden'}
                   onClick={() => onNavigate('hidden')} svg={<Ico d={PATH.eyeOff} />} />}
          <NavItem label="Review" active={view === 'anomalies'}
                   onClick={() => onNavigate('anomalies')} svg={<Ico d={PATH.calendarCheck} />} />
          {/* Connecting a vendor is self-service for the people who own the
              tenant — they should not have to ask us to add their own backups. */}
          {/* Everyone can read and write knowledge. Restricting it to managers
              would mean the people who actually work the board - and who learn
              these things first - cannot write them down. */}
          {/* Ungated on purpose. A mention is addressed to the person
              reading it and the callable only ever returns their own, so
              there is nobody to withhold it from. */}
          <NavItem label="Mentions" active={view === 'mentions'}
                   onClick={() => onNavigate('mentions')} svg={<Ico d={PATH.book} />} />
          <NavItem label="Knowledge" active={view === 'knowledge'}
                   onClick={() => onNavigate('knowledge')} svg={<Ico d={PATH.book} />} />
          {/* Ungated on purpose. Damon, 2026-08-13: reports should be on by
              default for all users. A report is a view of the same board a
              viewer can already read machine by machine, and the callable
              still applies that member's client scope. */}
          <NavItem label="Reports" active={view === 'reports'}
                   onClick={() => onNavigate('reports')} svg={<Ico d={PATH.calendarCheck} />} />
        </div>

        {/* Configuration is what you set once and revisit rarely; Activity above
            is the queue you work. Mixing them meant Team Members sat between
            Review and nothing, reading as a thing to check daily. Managing
            people, policies, alerting and credentials is a manager job, so the
            whole group is hidden rather than shown and then refused. */}
        {isManager && (
          <div className="nav-group">
            <div className="nav-group-label">Configuration</div>
            {/* Beside Team Members: one page is who is on the team, the
                other is what the words next to their names mean. */}
            <NavItem label="Team Members" active={view === 'team'}
                     onClick={() => onNavigate('team')} svg={<Ico d={PATH.people} />} />
            <NavItem label="Roles" active={view === 'roles'}
                     onClick={() => onNavigate('roles')} svg={<Ico d={PATH.key} />} />
            {/* Portal logins sit next to Team Members and Roles because they
                are the third answer to "who can see this" - but they are a
                separate page on purpose. Adding a technician and handing a
                customer a login are different powers, gated on different
                capabilities, and one screen for both invites the slip. */}
            <NavItem label="Portal Logins" active={view === 'clientusers'}
                     onClick={() => onNavigate('clientusers')} svg={<Ico d={PATH.people} />} />
            <NavItem label="Policies" active={view === 'policies'}
                     onClick={() => onNavigate('policies')} svg={<Ico d={PATH.calendarCheck} />} />
            {/* Promoted out of Settings. Who gets told, and when, is not a
                footnote to the build number - it is the reason the product
                works while nobody is watching the board. */}
            <NavItem label="Alerts" active={view === 'alerts'}
                     onClick={() => onNavigate('alerts')} svg={<Ico d={PATH.bell} />} />
            <NavItem label="Integrations" active={view === 'integrations'}
                     onClick={() => onNavigate('integrations')} svg={<Ico d={PATH.plug} />} />
          </div>
        )}
      </div>

      <div className="sidebar-foot">
        {/* Icon utilities sit ABOVE Sync now: they are navigation, and the sync
            button is an action. The bug reporter mounts into #bugtracker-mount
            so it sits inline beside Settings rather than floating over the page.
            Room here for Help and AI. */}
        <div className="util-row">
          <button
            className={'util-btn' + (view === 'settings' ? ' active' : '')}
            title="Settings" aria-label="Settings"
            onClick={() => onNavigate('settings')}
          >
            <Ico d={PATH.cog} />
          </button>
          <button
            className="util-btn" onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
            aria-label="Toggle theme"
          >
            <Ico d={theme === 'dark' ? PATH.sun : PATH.moon} />
          </button>
          <AiButton onClick={onAi} />
          <button
            className="util-btn" onClick={onFeatureRequest}
            title="Feature requests" aria-label="Feature requests"
          >
            <Ico d={PATH.bulb} />
          </button>
          <div id="bugtracker-mount" className="util-mount" title="Report a bug" />
          {/* To the RIGHT of the bug reporter, which is where Damon asked for
              it and where it belongs: the two are asked in the same breath -
              "how does this work" and "this is not working". */}
          <button
            className={'util-btn' + (view === 'help' ? ' active' : '')}
            title="Help" aria-label="Help"
            onClick={() => onNavigate('help')}
          >
            <Ico d={PATH.help} />
          </button>
        </div>

        <button className="btn btn-sync" onClick={onSyncNow} disabled={syncing}>
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>

        {/* WHEN the data is from, directly under the button that refreshes it.
            Without it, a dashboard that quietly stopped collecting looks exactly
            like one where nothing has changed - which is the failure this whole
            product exists to prevent. */}
        <div className="sync-when" title={syncTitle(summary)}>{syncWhen(summary)}</div>

        {email && (
          <div className="sidebar-user">
            <span className="sidebar-email" title={email}>{email}</span>
            <button className="btn-link" onClick={onSignOut}>Sign out</button>
          </div>
        )}

        <div className="sidebar-version" title={`Build ${__BUILD_ID__}\nBuilt ${__BUILT_AT__}`}>
          v{__APP_VERSION__}
        </div>
      </div>
    </nav>
  );
}

const SOURCES: Source[] = ['datto', 'sp', 'sc', 'msp', 'unitrends', 'email'];

/** Newest successful write across all sources, in ms. 0 when never. */
function newestSync(summary: Summary | null): number {
  if (!summary) return 0;
  let newest = 0;
  for (const src of SOURCES) {
    const ts = summary.lastSyncedAt?.[src];
    const ms = ts?.toDate?.()?.getTime?.() ?? 0;
    if (ms > newest) newest = ms;
  }
  return newest;
}

/**
 * How fresh the board is.
 *
 * Deliberately reports the last successful WRITE, not the last attempt. An
 * attempt that failed leaves the previous data on screen, and saying "synced
 * just now" over stale numbers is the single most misleading thing this UI
 * could do. When the most recent attempt failed, that is called out separately.
 */
function syncWhen(summary: Summary | null): string {
  const ms = newestSync(summary);
  if (!ms) return 'Not synced yet';
  const mins = (Date.now() - ms) / 60000;
  if (mins < 1.5) return 'Synced just now';
  if (mins < 60) return `Synced ${Math.round(mins)}m ago`;
  if (mins < 1440) return `Synced ${Math.round(mins / 60)}h ago`;
  return `Synced ${Math.round(mins / 1440)}d ago`;
}

function syncTitle(summary: Summary | null): string {
  const ms = newestSync(summary);
  const failing = SOURCES.filter((src) => summary?.lastSyncOk?.[src] === false);
  const when = ms ? new Date(ms).toLocaleString() : 'never';
  return failing.length
    ? `Last successful sync: ${when}\nMost recent attempt FAILED for: ${failing.join(', ')}`
    : `Last successful sync: ${when}`;
}

function NavItem({
  label, icon, wide, svg, active, onClick, counts, stale,
}: {
  label: string;
  icon?: string;
  /** The logo is a wordmark rather than a square mark. */
  wide?: boolean;
  svg?: React.ReactNode;
  active: boolean;
  onClick: () => void;
  counts?: StatusCounts | undefined;
  stale?: boolean;
}) {
  const failed = counts?.failed ?? 0;
  const warning = counts?.warning ?? 0;

  return (
    <button className={'nav-item' + (active ? ' active' : '')} onClick={onClick}>
      {/* `wide` opts a wordmark-shaped logo into a wider box. StorageCraft is
          286x176 where every other mark is square, and without this it either
          overruns the others or gets shrunk to fit their shape. */}
      {/* No width attribute: it said 36 while the CSS said 32, and the next
          person to read it would size the slot from the wrong number. */}
      {icon && <img className={'nav-icon' + (wide ? ' wide' : '')} src={icon} alt="" />}
      {svg}
      <span className="nav-label">{label}</span>
      <span className="nav-badges">
        {stale && <span className="nav-badge nav-badge-stale" title="Last sync failed">!</span>}
        {failed > 0 && <span className="nav-badge nav-badge-fail">{failed}</span>}
        {failed === 0 && warning > 0 && <span className="nav-badge nav-badge-warn">{warning}</span>}
      </span>
    </button>
  );
}
